/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AuthenticationTypeV3 } from "@tivocorp/data-domain-lib";
import type { AuthConfigurationV3 } from "@tivocorp/data-domain-lib";

import { Log } from "../logging";
import { SignInFlowEvent, SignInLogger } from "../logging/SignInLogger";
import { AppStartupInteractor, SignInInteractor } from "../interactor";
import { DeviceModel } from "../device";
import { DiagnosticSeverity } from "../types";
import { TIVO_SERVICES } from "../../config";

const TAG: string = "auth/authSchemes";

const SCHEME_FALLBACK_MESSAGE: string =
  "Authorization schemes resolved below the live auth-config response";

/**
 * `tier` values on auth_scheme_fallback diagnostics — how far down the
 * resolution chain the schemes actually came from. The first tier (the live
 * configs already in the store) is the normal path and is not logged.
 */
const SCHEME_TIER_LAST_AUTH_CONTEXT: string = "last_auth_context";
const SCHEME_TIER_CONFIG_FETCH: string = "config_fetch";
const SCHEME_TIER_DEFAULTS: string = "defaults";

/** Emits the auth_scheme_fallback diagnostic for one resolution tier. */
function logSchemeFallback(
  tier: string,
  authType: string,
  severity: DiagnosticSeverity
): void {
  SignInLogger.logFlowEvent(
    SignInFlowEvent.AuthSchemeFallback,
    SCHEME_FALLBACK_MESSAGE,
    severity,
    { tier: tier, auth_type: authType }
  );
}

/**
 * Authorization schemes for one authentication type, exactly as the
 * auth-config service advertises them
 * (`GET /v3/authConfigurations` → `authorizationSchemes`).
 */
export interface TAuthorizationSchemes {
  claim: string;
  bind: string;
  domainToken: string;
}

/**
 * Last-resort schemes, used only when the auth-config service has told us
 * nothing at all: no configs in the store, no context persisted from a
 * previous sign-in, and the on-demand config fetch failed too. Attempting a
 * refresh with these beats not attempting one, but every normal path reads
 * the live values via {@link resolveAuthorizationSchemes}.
 */
export const DEFAULT_AUTH_SCHEMES: TAuthorizationSchemes = {
  claim: "client-shared-key",
  bind: "client-shared-key-custom-idp",
  domainToken: "client-shared-key"
};

/**
 * Picks the authorization schemes for `authType` out of an auth-config
 * service response. Returns null when the response carries no entry for it.
 */
export function findAuthorizationSchemes(
  configs: AuthConfigurationV3[] | undefined | null,
  authType: string
): TAuthorizationSchemes | null {
  if (configs === undefined || configs === null) {
    return null;
  }
  const match: AuthConfigurationV3 | undefined = configs.find(
    (config: AuthConfigurationV3): boolean =>
      config.authenticationType === authType
  );
  if (match === undefined || match.authorizationSchemes === undefined) {
    return null;
  }
  return match.authorizationSchemes;
}

/**
 * Resolves the authorization schemes for `authType` from the auth-config
 * service, in order of preference:
 *
 *   1. the configs already in the store — the live ACS response for this app
 *      session, available to every thunk;
 *   2. the context persisted at the last successful sign-in — the same ACS
 *      response from the previous session, which is all a cold-boot token
 *      refresh has to work with;
 *   3. a fresh ACS fetch, for a refresh that runs before sign-in state has
 *      been rebuilt.
 *
 * Returns null when the service cannot be reached at all; callers then fall
 * back to {@link DEFAULT_AUTH_SCHEMES}.
 */
export async function resolveAuthorizationSchemes(
  authType: string,
  configs?: AuthConfigurationV3[]
): Promise<TAuthorizationSchemes | null> {
  const fromStore: TAuthorizationSchemes | null = findAuthorizationSchemes(
    configs,
    authType
  );
  if (fromStore !== null) {
    return fromStore;
  }

  const lastAuthContext = await DeviceModel.getLastAuthContext();
  if (
    lastAuthContext !== undefined &&
    lastAuthContext !== null &&
    lastAuthContext.authenticationType === authType
  ) {
    logSchemeFallback(
      SCHEME_TIER_LAST_AUTH_CONTEXT,
      authType,
      DiagnosticSeverity.Info
    );
    return lastAuthContext.authorizationSchemes;
  }

  try {
    Log.d(
      TAG,
      "resolveAuthorizationSchemes() fetching auth configurations for " +
        authType
    );
    const response = await new SignInInteractor().fetchAuthConfigurationsV3({
      msoName: DeviceModel.getTiVoSettings().getMsoName()
    });
    logSchemeFallback(
      SCHEME_TIER_CONFIG_FETCH,
      authType,
      DiagnosticSeverity.Info
    );
    return findAuthorizationSchemes(response.authconfigurations, authType);
  } catch (error: unknown) {
    Log.e(TAG, "resolveAuthorizationSchemes() config fetch failed", error);
    return null;
  }
}

/**
 * Authorization schemes for the DBS license-plate binding flow, used by the
 * DBS renewal branch of the token refresh where no thunk state is available.
 * `configs` is the auth-config response the refresh fetched, when it has one.
 */
export async function resolveDbsSchemes(
  configs?: AuthConfigurationV3[]
): Promise<TAuthorizationSchemes> {
  const schemes: TAuthorizationSchemes | null =
    await resolveAuthorizationSchemes(
      AuthenticationTypeV3.DbsLicensePlateBinding,
      configs
    );
  if (schemes !== null) {
    return schemes;
  }
  logSchemeFallback(
    SCHEME_TIER_DEFAULTS,
    AuthenticationTypeV3.DbsLicensePlateBinding,
    DiagnosticSeverity.Warning
  );
  return DEFAULT_AUTH_SCHEMES;
}

/**
 * Picks the IP auto-login endpoint out of an auth-config service response —
 * `idpMetaData.authenticationUrl` on the tivoIpAutoLoginIdp entry. Returns
 * null when the response carries no such entry, or advertises no URL on it.
 */
export function findIpAutoLoginAuthenticationUrl(
  configs: AuthConfigurationV3[] | undefined | null
): string | null {
  if (configs === undefined || configs === null) {
    return null;
  }
  const match: AuthConfigurationV3 | undefined = configs.find(
    (config: AuthConfigurationV3): boolean =>
      config.authenticationType === AuthenticationTypeV3.TivoIpAutoLoginIdp
  );
  if (
    match === undefined ||
    match.idpMetaData === undefined ||
    match.idpMetaData === null
  ) {
    return null;
  }
  // Typed as required in the schema, but a live response can still omit it.
  const authenticationUrl: string | undefined =
    match.idpMetaData.authenticationUrl;
  if (authenticationUrl === undefined || authenticationUrl.length === 0) {
    return null;
  }
  return authenticationUrl;
}

/**
 * Resolves the IP auto-login endpoint, in order of preference:
 *
 *   1. the configs already in the store — the live ACS response for this app
 *      session, which is what the sign-in flow hands in;
 *   2. a fresh ACS fetch, for a token refresh that runs before sign-in state
 *      has been rebuilt (the endpoint is not persisted with the last auth
 *      context, unlike the authorization schemes);
 *   3. the TSRI endpoints catalog entry for the identity auth service, for an
 *      MSO whose ACS response advertises no authenticationUrl at all.
 *
 * Returns null when none of the three resolves; the caller then rejects with
 * IPL.NO_ENDPOINT rather than calling the interactor with a blank URL.
 */
export async function resolveIpAutoLoginAuthenticationUrl(
  configs?: AuthConfigurationV3[]
): Promise<string | null> {
  const fromStore: string | null = findIpAutoLoginAuthenticationUrl(configs);
  if (fromStore !== null) {
    return fromStore;
  }

  try {
    Log.d(
      TAG,
      "resolveIpAutoLoginAuthenticationUrl() fetching auth configurations"
    );
    const response = await new SignInInteractor().fetchAuthConfigurationsV3({
      msoName: DeviceModel.getTiVoSettings().getMsoName()
    });
    const fromFetch: string | null = findIpAutoLoginAuthenticationUrl(
      response.authconfigurations
    );
    if (fromFetch !== null) {
      return fromFetch;
    }
  } catch (error: unknown) {
    Log.e(
      TAG,
      "resolveIpAutoLoginAuthenticationUrl() config fetch failed",
      error
    );
  }

  try {
    const endpoint: string =
      await new AppStartupInteractor().getServiceEndpointByServiceName(
        TIVO_SERVICES.TivoIdentityAuthService
      );
    if (endpoint !== undefined && endpoint !== null && endpoint.length > 0) {
      Log.w(
        TAG,
        "resolveIpAutoLoginAuthenticationUrl() no authenticationUrl " +
          "advertised, using the TSRI endpoints catalog"
      );
      return endpoint;
    }
  } catch (error: unknown) {
    Log.e(
      TAG,
      "resolveIpAutoLoginAuthenticationUrl() endpoint lookup failed",
      error
    );
  }

  return null;
}

/**
 * Domain-token scheme for a silent refresh.
 *
 * The refresh presents the device's primary credential, so the scheme is the
 * `domainToken` one the auth-config service advertises for the authentication
 * type this device signed in with, in order of preference:
 *
 *   1. `configs` — the auth-config response the refresh fetched just now;
 *   2. the context persisted at sign-in;
 *   3. {@link resolveAuthorizationSchemes} for TiVo native, then the defaults.
 */
export async function resolveDomainTokenScheme(
  configs?: AuthConfigurationV3[]
): Promise<string> {
  const lastAuthContext = await DeviceModel.getLastAuthContext();
  const authType: string =
    lastAuthContext !== undefined && lastAuthContext !== null
      ? lastAuthContext.authenticationType
      : AuthenticationTypeV3.TivoNativeIdp;
  const fromConfigs: TAuthorizationSchemes | null = findAuthorizationSchemes(
    configs,
    authType
  );
  if (fromConfigs !== null && fromConfigs.domainToken.length > 0) {
    return fromConfigs.domainToken;
  }

  if (
    lastAuthContext !== undefined &&
    lastAuthContext !== null &&
    lastAuthContext.authorizationSchemes.domainToken.length > 0
  ) {
    return lastAuthContext.authorizationSchemes.domainToken;
  }

  const schemes: TAuthorizationSchemes | null =
    await resolveAuthorizationSchemes(
      AuthenticationTypeV3.TivoNativeIdp,
      configs
    );
  if (schemes !== null && schemes.domainToken.length > 0) {
    return schemes.domainToken;
  }
  logSchemeFallback(
    SCHEME_TIER_DEFAULTS,
    AuthenticationTypeV3.TivoNativeIdp,
    DiagnosticSeverity.Warning
  );
  return DEFAULT_AUTH_SCHEMES.domainToken;
}
